import {
  Card,
  CardHeader,
  CardContent,
  Stack,
  TextField,
  CardActions,
  Modal,
  FormControlLabel,
  Switch,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import PropTypes from 'prop-types';
import { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useDispatch, useSelector } from 'react-redux';
import { departmentEditSchema } from '../schemas/departmentSchemas';
import { updateDepartment, resetDepartmentState } from '../store/departmentActions';

DepartmentEditForm.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  department: PropTypes.object,
};

export default function DepartmentEditForm({ department, open, onClose }) {
  const dispatch = useDispatch();
  const { isLoading, isSuccess } = useSelector((state) => state.departmentReducer);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: { name: '', code: '', is_active: true },
    resolver: yupResolver(departmentEditSchema),
  });

  useEffect(() => {
    if (department) {
      reset({
        name: department.name || '',
        code: department.code || '',
        is_active: department.is_active ?? true,
      });
    }
  }, [department, reset]);

  useEffect(() => {
    if (isSuccess) onClose();
  }, [isSuccess, onClose]);

  const onSubmit = (values) => {
    if (!department?.id) return;
    dispatch(updateDepartment({ id: department.id, payload: { ...values, code: values.code || null } }));
  };

  const handleClose = () => {
    reset();
    dispatch(resetDepartmentState());
    onClose();
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <Card
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '340px', md: '500px' },
          bgcolor: 'background.paper',
          borderRadius: '10px',
          boxShadow: 24,
        }}
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <CardHeader title="Edit Department" />
          <CardContent>
            <Stack spacing={3}>
              <Controller
                name="name"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Department Name"
                    error={Boolean(errors.name)}
                    helperText={errors.name?.message}
                  />
                )}
              />
              <Controller
                name="code"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Department Code (optional)"
                    error={Boolean(errors.code)}
                    helperText={errors.code?.message}
                  />
                )}
              />
              <Controller
                name="is_active"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={<Switch checked={field.value} onChange={(e) => field.onChange(e.target.checked)} />}
                    label="Active"
                  />
                )}
              />
            </Stack>
          </CardContent>
          <CardActions sx={{ p: 3, pt: 0 }}>
            <Stack direction="row" spacing={2} sx={{ width: '100%', justifyContent: 'flex-end' }}>
              <LoadingButton variant="outlined" onClick={handleClose} disabled={isLoading}>
                Cancel
              </LoadingButton>
              <LoadingButton type="submit" variant="contained" loading={isLoading} disabled={isSubmitting}>
                Update Department
              </LoadingButton>
            </Stack>
          </CardActions>
        </form>
      </Card>
    </Modal>
  );
}
